import { useEffect, useRef, useState } from "react";
import { ApiError } from "../../api/client";
import type { Authorization, GeneralResponse } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatEuro, parseAmount } from "../../lib/money";
import { HoursTable } from "../../ui/HoursTable";
import { SaveInput } from "../../ui/SaveInput";
import { ReceiptFoot, ReceiptHead } from "../orders/Receipt";
import { amountText } from "../zones-admin/zones";
import { BancontactCard } from "./BancontactCard";
import { ReceiptQrCard } from "./ReceiptQrCard";
import { DiscountsCard } from "./DiscountsCard";
import {
  checkoutDiscounts,
  emailOk,
  ownGeneral,
  readMinutes,
  textLines,
  type OwnGeneral,
  type ShopDetails,
} from "./general";
import { typedPhone } from "../../lib/phone";

interface Props {
  /** The owner's Beheer code. */
  authorization: Authorization;
}

/** A closed day that opens again gets the shop's usual evening. */
const EVENING: [string, string] = ["17:00", "23:00"];

/** A text field of the receipt (one line per line), saved when it is left. */
function SaveTextarea({ value, onSave }: { value: string; onSave: (text: string) => void }) {
  const [text, setText] = useState<string | null>(null);
  return (
    <textarea
      rows={2}
      value={text ?? value}
      onChange={(event) => {
        setText(event.target.value);
      }}
      onBlur={() => {
        if (text !== null && text !== value) onSave(text);
        setText(null);
      }}
    />
  );
}

/**
 * Beheer › Algemeen, as in the prototype (owner): the shop's details, the opening hours, the
 * preparation times, the receipt's header and footer, bank-QR and tips in the Tafo app, and the change
 * float. Every field is saved when it is left and holds at once on every device; the receipt on the
 * right shows the result.
 *
 * Of the shop's details the owner changes the phone number and the e-mail address (Furkan, 03/10/2026).
 * The names, the firm, the address, the VAT number and the IBAN are shown as Tafo set them: they say
 * who sells, on every receipt and in the bank-QR, and only Tafo changes them.
 */
export function GeneralScreen({ authorization }: Props) {
  const { api, status, general: live, generalChanged, report, toast, reportSettingsRevision } = useTill();
  const [data, setData] = useState<GeneralResponse | null>(null);
  const [message, setMessage] = useState("");
  /** The newest save: the answer to an older one never puts back what was changed since (a quick on-off). */
  const latest = useRef(0);
  /** Counts the changes of the key of Bancontact Pro: Algemeen then reads again whether the QR confirms itself. */
  const [keyChanges, setKeyChanges] = useState(0);
  const text = t.general;

  // again when Algemeen or the float changed on another device
  useEffect(() => {
    let current = true;
    api
      .request<GeneralResponse>("GET", "/device/general", { retry: true })
      .then((answer) => {
        if (current) setData(answer);
      })
      .catch(report);
    return () => {
      current = false;
    };
  }, [api, report, live, reportSettingsRevision, keyChanges]);

  if (data === null) {
    return (
      <div className="panel admin-section general-admin">
        <h2>{text.title}</h2>
      </div>
    );
  }
  const general = data.general;

  /** Saves at once; a refusal of the server shows its reason. Resolves whether it was saved. */
  const save = async (change: Partial<OwnGeneral>, floatCents = data.floatCents): Promise<boolean> => {
    const before = data;
    const mine = ++latest.current;
    setData({ ...data, general: { ...general, ...change }, floatCents });
    try {
      const answer = await api.request<GeneralResponse>("PUT", "/device/general", {
        staffToken: authorization.token,
        body: { general: ownGeneral({ ...general, ...change }), floatCents },
      });
      if (mine === latest.current) {
        setData(answer);
        generalChanged(answer.general);
      }
      toast(floatCents === before.floatCents ? text.saved : text.drawer.saved(formatEuro(floatCents)));
      return true;
    } catch (error) {
      if (mine === latest.current) setData(before);
      if (error instanceof ApiError && error.status === 400) setMessage(error.message);
      else report(error);
      return false;
    }
  };

  /** A detail only Tafo changes: shown as it is, to read and to copy. */
  const fixed = (key: keyof ShopDetails, label: string) => (
    <label className="fixed">
      {label}
      <input value={general[key]} readOnly />
    </label>
  );

  /** A field of Zaakgegevens the shop changes itself: checked as in the prototype, the reason below the fields. */
  const field = (
    key: "phone" | "email",
    label: string,
    check: (value: string) => string | null,
    { wide = false, ...options }: { inputMode?: "tel" | "email"; placeholder?: string; wide?: boolean } = {},
  ) => (
    <label className={wide ? "wide" : undefined}>
      {label}
      <SaveInput
        value={general[key]}
        {...options}
        onSame={() => {
          setMessage("");
        }}
        onSave={(typed) => {
          const value = key === "phone" ? typedPhone(typed) : typed.trim();
          const problem = check(value);
          setMessage(problem ?? "");
          if (problem !== null) return false;
          void save(key === "phone" ? { phone: value } : { email: value });
          return true;
        }}
      />
    </label>
  );
  const errors = text.errors;
  const free = () => null;

  return (
    <div className="panel admin-section general-admin">
      <h2>{text.title}</h2>
      <div className="genwrap">
        <div className="genform">
          <div className="zbox">
            <h3>{text.shop.title}</h3>
            <div className="sfrm">
              {fixed("receiptName", text.shop.receiptName)}
              {fixed("displayName", text.shop.displayName)}
              {fixed("company", text.shop.company)}
              {fixed("street", text.shop.street)}
              {fixed("postcode", text.shop.postcode)}
              {fixed("city", text.shop.city)}
              {field("phone", text.shop.phone, free, { inputMode: "tel" })}
              {field("email", text.shop.email, (value) => (value && !emailOk(value) ? errors.email : null), {
                inputMode: "email",
                placeholder: text.shop.emailPlaceholder,
                // a long address did not fit in one column (Furkan, 05/10/2026)
                wide: true,
              })}
              {fixed("vatNumber", text.shop.vatNumber)}
              {fixed("iban", text.shop.iban)}
            </div>
            <p className="pinmsg left" role="alert">
              {message}
            </p>
          </div>

          <div className="zbox">
            <h3>{text.hours.title}</h3>
            <HoursTable
              hours={general.openingHours}
              reopen={EVENING}
              onChange={(openingHours) => void save({ openingHours })}
            />
          </div>

          <div className="zbox">
            <h3>{text.prep.title}</h3>
            <div className="inl gen-inl">
              {text.prep.takeaway}{" "}
              <SaveInput
                value={String(general.prepMinutes.takeaway)}
                label={text.prep.takeaway}
                className="num gen-min"
                inputMode="numeric"
                onSave={(typed) => {
                  const minutes = readMinutes(typed);
                  if (minutes === null) toast(errors.minutes, "bad");
                  else void save({ prepMinutes: { ...general.prepMinutes, takeaway: minutes } });
                }}
              />{" "}
              {text.prep.minutes} &nbsp; {text.prep.delivery}{" "}
              <SaveInput
                value={String(general.prepMinutes.delivery)}
                label={text.prep.delivery}
                className="num gen-min"
                inputMode="numeric"
                onSave={(typed) => {
                  const minutes = readMinutes(typed);
                  if (minutes === null) toast(errors.minutes, "bad");
                  else void save({ prepMinutes: { ...general.prepMinutes, delivery: minutes } });
                }}
              />{" "}
              {text.prep.minutes}
            </div>
            <p className="note">{text.prep.note}</p>
          </div>

          <div className="zbox">
            <h3>{text.receipt.title}</h3>
            <label className="inl gen-check">
              <input
                type="checkbox"
                checked={general.showHours}
                onChange={(event) => void save({ showHours: event.target.checked })}
              />{" "}
              {text.receipt.showHours}
            </label>
            <label className="gen-lines">
              <span>
                {text.receipt.header} <span className="proto">{text.receipt.headerHint}</span>
              </span>
              <SaveTextarea
                value={general.headerLines.join("\n")}
                onSave={(typed) => void save({ headerLines: textLines(typed) })}
              />
            </label>
            <label className="gen-lines">
              <span>
                {text.receipt.footer} <span className="proto">{text.receipt.footerHint}</span>
              </span>
              <SaveTextarea
                value={general.footerLines.join("\n")}
                onSave={(typed) => void save({ footerLines: textLines(typed) })}
              />
            </label>
            <p className="note">{text.receipt.note}</p>
          </div>

          <div className="zbox">
            <h3>{text.discounts.title}</h3>
            <DiscountsCard discounts={checkoutDiscounts(general)} onSave={(discounts) => save({ discounts })} />
          </div>

          <div className="zbox">
            <h3>{text.qr.title}</h3>
            <label className="inl gen-check">
              <input
                type="checkbox"
                checked={general.bankQr.courier}
                onChange={(event) => void save({ bankQr: { ...general.bankQr, courier: event.target.checked } })}
              />{" "}
              {text.qr.courier}
            </label>
            <label className="inl gen-check">
              <input
                type="checkbox"
                checked={general.bankQr.waiter}
                onChange={(event) => void save({ bankQr: { ...general.bankQr, waiter: event.target.checked } })}
              />{" "}
              {text.qr.waiter}
            </label>
            <label className="inl gen-check">
              <input
                type="checkbox"
                checked={general.tips}
                onChange={(event) => void save({ tips: event.target.checked })}
              />{" "}
              {text.qr.tips}
            </label>
            <label className="inl gen-check">
              <input
                type="checkbox"
                checked={general.appHours === true}
                onChange={(event) => void save({ appHours: event.target.checked })}
              />{" "}
              {text.qr.appHours}
            </label>
            {/* no explanation any more (Furkan, 18/10/2026); only what stops the QR from working */}
            {!data.bankQrAutomatic && !general.iban && (
              <p className="note">
                <b>{text.qr.noIban}</b>
                {text.qr.noIbanAfter}
              </p>
            )}
            <BancontactCard
              authorization={authorization}
              onChanged={() => {
                setKeyChanges((count) => count + 1);
              }}
            />
            <ReceiptQrCard authorization={authorization} />
          </div>

          <div className="zbox">
            <h3>{text.drawer.title}</h3>
            <div className="inl gen-inl">
              {text.drawer.float}{" "}
              <SaveInput
                value={amountText(data.floatCents)}
                label={text.drawer.float}
                className="num gen-float"
                inputMode="decimal"
                onSave={(typed) => {
                  const cents = parseAmount(typed);
                  if (cents !== null) void save({}, cents);
                }}
              />
            </div>
          </div>
        </div>

        <div className="genprev">
          <div className="proto">{text.preview}</div>
          <div className="bonwrap">
            <article className="bon" aria-label={text.preview}>
              <ReceiptHead shop={status.shop} general={general} />
              <div className="c muted gen-order">{text.previewOrder}</div>
              <ReceiptFoot general={general} />
            </article>
          </div>
        </div>
      </div>
    </div>
  );
}
